/* @flow */

import React from 'react';
import glamorous from 'glamorous';
import colorUtils from 'color';
import { padding } from 'polished';

import AnimateNumber from 'app/components/commons/AnimateNumber';
import PlayerBar from 'app/components/challenge/PlayerBar';
import PlayerBarValue from 'app/components/challenge/PlayerBarValue';
import PlayerLabels from 'app/components/challenge/PlayerLabels';
import StatusIcon from 'app/components/challenge/StatusIcon';
import { IN_PROGRESS, SCHEDULED } from 'app/constants/challengeStatusTypes';
import { hover } from '../../utils/cssUtils';
import LivePlayerWidget from '../../widgets/one-sport/LivePlayerWidget';
import Tappable from '../commons/Tappable';
import AnimatePlayerRow from './AnimatePlayerRow';
import LineGraph from './LineGraph';
import PlayerAvatar from './PlayerAvatar';
import { isInProgress } from '../../utils/challengeUtils';

type Props = {
  awayTeam?: string,
  bonus?: {
    name?: string,
    key?: string
  },
  dropped?: boolean,
  gameOrder?: number,
  gamePlaced?: boolean,
  highlight?: boolean,
  homeTeam?: string,
  id: string,
  idStr: string,
  image?: string,
  initials?: string,
  isHome: boolean,
  isTeam: boolean,
  isRacing?: boolean,
  isScratched?: boolean,
  labels: Array<Object>,
  lineupId?: number,
  maskPlayer?: boolean,
  name?: string,
  number?: number,
  onWidgetClick?: (id: string) => void,
  popularity?: number,
  positionId?: string,
  position?: string,
  progress?: string,
  reverseOrder?: boolean,
  rider?: string,
  riderImage?: string,
  score: number,
  showWidget?: boolean,
  statusId?: number,
  trainer?: string,
  twin?: boolean,
  twoUp?: boolean,
  win?: number,
  place?: number,
  throttle?: boolean
};

const EntityWrapper = glamorous.div(
  ({ theme, twin }) => ({
    position: 'relative',
    opacity: twin ? 0.4 : 1,
    backgroundColor: twin ? 'transparent' : theme.color.white,
    borderColor: theme.color.basis6,
    borderStyle: 'solid',
    borderWidth: 0,
    fontSize: '0.9rem',
    color: theme.color.basis1,
    display: 'flex',
    alignItems: 'center',
    ...padding(0, 10),
    minWidth: 0,
    minHeight: 54,
    borderBottomWidth: 1
  }),
  ({ disabled, theme }) =>
    !disabled
      ? hover({
          backgroundColor: colorUtils(theme.color.white)
            .darken(0.05)
            .hex()
        })
      : {}
);

const BarFinal = glamorous.div(
  {
    display: 'flex',
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'space-between',
    padding: '6px 0'
  },
  ({ compressed }) =>
    compressed
      ? {
          flexDirection: 'column',
          alignItems: 'flex-end'
        }
      : {
          flexDirection: 'row-reverse',
          alignItems: 'center'
        }
);

const Stack = glamorous.div(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  ({ compressed }) =>
    !compressed
      ? {
          minWidth: 70
        }
      : {}
);

const PlayerBarInfo = glamorous.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  fontSize: theme.fontSize.small
}));

const PlayerProgress = glamorous.div({
  opacity: 0.7,
  paddingLeft: '0.25em',
  fontSize: '0.833em'
});

const StatusIconWrapper = glamorous.div(
  {
    color: 'white',
    fontSize: '0.5rem',
    height: '2.2em',
    left: 0,
    padding: 1,
    position: 'absolute',
    top: 0,
    transform: 'rotate(360deg)', // stop jitter effect
    width: '2.2em'
  },
  ({ live, theme }) =>
    live
      ? {
          backgroundImage: `linear-gradient(135deg, ${theme.color.primary} 50%, transparent 50%)`
        }
      : {
          backgroundImage: `linear-gradient(135deg, ${theme.color.basis1} 50%, transparent 50%)`
        }
);

const AvatarCell = glamorous.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  ...padding(null, 5),
  position: 'relative',
  [theme.mediaQuery.mobileOnly]: {
    display: 'none'
  },
  [theme.mediaQuery.desktopOnly]: {
    display: 'none'
  }
}));

const PopularityWrapper = glamorous.div(({ compressed }) => ({
  padding: '3px 0',
  flexGrow: 1,
  maxWidth: compressed ? 50 : 70,
  width: 50,
  fontSize: '0.8rem',
  textAlign: 'center'
}));

const BlankPlayerBar = glamorous.div(
  {
    display: 'flex',
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0, // flexbox hack
    paddingLeft: '0.25em',
    paddingRight: '0.5em'
  },
  ({ theme }) => ({
    paddingTop: theme.spacing.xsmall,
    paddingBottom: theme.spacing.xsmall
  })
);

class ClosedEntityRow extends React.Component<Props> {
  props: Props;

  ref = React.createRef(); // eslint-disable-line react/sort-comp

  static defaultProps = {
    awayTeam: '',
    bonus: undefined,
    dropped: false,
    gameOrder: undefined,
    gamePlaced: false,
    homeTeam: '',
    image: undefined,
    initials: undefined,
    isRacing: false,
    isScratched: false,
    highlight: false,
    lineupId: undefined,
    maskPlayer: false,
    name: undefined,
    number: undefined,
    onWidgetClick: undefined,
    popularity: undefined,
    positionId: undefined,
    position: undefined,
    progress: undefined,
    reverseOrder: false,
    rider: undefined,
    riderImage: undefined,
    showWidget: false,
    statusId: undefined,
    trainer: undefined,
    twin: false,
    twoUp: false,
    win: undefined,
    place: undefined,
    throttle: false
  };

  handleWidgetClick = () => {
    const { lineupId = '', idStr, onWidgetClick } = this.props;

    if (onWidgetClick) {
      onWidgetClick(`${lineupId}${idStr}`);
    }
  };

  render() {
    const {
      awayTeam,
      bonus,
      dropped,
      gameOrder,
      gamePlaced,
      highlight,
      homeTeam,
      id,
      image,
      initials,
      isHome,
      isRacing,
      isScratched,
      isTeam,
      labels,
      lineupId,
      maskPlayer,
      name,
      number,
      popularity,
      positionId,
      position,
      progress,
      reverseOrder,
      rider,
      riderImage,
      score,
      showWidget,
      statusId,
      trainer,
      twin,
      twoUp,
      win,
      place,
      throttle
    } = this.props;

    if (maskPlayer) {
      return (
        <EntityWrapper>
          <StatusIconWrapper live={isInProgress(statusId)}>
            <StatusIcon statusId={statusId || SCHEDULED} />
          </StatusIconWrapper>
          <BlankPlayerBar>
            {isRacing ? "Horse's Leg Not Started" : "Player's Game Not Started"}
          </BlankPlayerBar>
        </EntityWrapper>
      );
    }

    const avatarImage = isRacing ? riderImage : image;

    return (
      <div>
        <AnimatePlayerRow value={score} target={this.ref.current}>
          <React.Fragment>
            <Tappable
              component={EntityWrapper}
              innerRef={this.ref}
              twin={twin}
              onClick={this.handleWidgetClick}
            >
              <StatusIconWrapper live={isInProgress(statusId)}>
                <StatusIcon statusId={statusId} />
              </StatusIconWrapper>
              {(avatarImage || initials) && !twoUp && (
                <AvatarCell>
                  <PlayerAvatar fallbackText={initials} image={avatarImage} />
                </AvatarCell>
              )}
              <PlayerBar
                awayTeam={awayTeam}
                bonus={bonus}
                homeTeam={homeTeam}
                gameOrder={gameOrder}
                isCaptain={positionId === 'captain'}
                isHome={isHome}
                isRacing={isRacing}
                isScratched={isScratched}
                highlight={highlight}
                name={name}
                reverseOrder={reverseOrder}
                rider={rider}
                position={position}
                number={number}
                trainer={trainer}
              />
              <BarFinal compressed={twoUp}>
                <Stack compressed={twoUp}>
                  <PlayerBarValue dropped={dropped}>
                    <AnimateNumber format="0.00" number={score} />
                  </PlayerBarValue>
                  <PlayerBarInfo>
                    <PlayerLabels items={labels} />
                    {isInProgress(statusId) && <PlayerProgress>{progress}</PlayerProgress>}
                  </PlayerBarInfo>
                </Stack>
                {popularity !== undefined && (
                  <PopularityWrapper compressed={twoUp}>
                    <LineGraph hideLabel={twoUp} value={popularity} />
                  </PopularityWrapper>
                )}
              </BarFinal>
            </Tappable>
            <LivePlayerWidget
              enablePolling={isInProgress(statusId)}
              id={id}
              isCompressed
              gamePlaced={gamePlaced}
              isRacing={isRacing}
              isTeam={isTeam}
              isOpen={showWidget}
              lineupId={lineupId}
              onClose={this.handleWidgetClick}
              win={win}
              place={place}
              throttle={throttle}
            />
          </React.Fragment>
        </AnimatePlayerRow>
      </div>
    );
  }
}

export default ClosedEntityRow;
